import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  ArrowLeft,
  User,
  CheckCircle,
  FileText,
  Target,
  Loader2,
  History
} from 'lucide-react';
import { apiClient } from '../api/client';

const EvaluationPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [comparison, setComparison] = useState<{ user_findings: string, ground_truth: string, patient_name: string, brief_feedback?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchComparison();
  }, [sessionId]);

  const fetchComparison = async () => {
    try {
      const res = await apiClient.get(`/api/sessions/${sessionId}/comparison`);
      setComparison(res.data);
    } catch (err) {
      console.error("Failed to fetch comparison:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleReturnToChart = async () => {
    try {
        const response = await apiClient.get('/api/auth/next-task');
        const { redirect, patient_id } = response.data;
        
        if (redirect === 'chart') {
          navigate(`/patient-chart/${patient_id}`);
        } else {
          navigate('/patient-chart/patient_0');
        }
    } catch (err) {
        navigate('/patient-chart/patient_0');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-primary text-primary-foreground shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReturnToChart}
            className="text-primary-foreground hover:bg-primary-foreground/10"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-bold">Encounter Completed</h1>
          <div className="flex-1"></div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/history')}
            className="text-primary-foreground hover:bg-primary-foreground/10 gap-2"
          >
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">My History</span>
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center bg-green-100 p-3 rounded-full mb-4">
                <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Interview with {comparison?.patient_name} Completed
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
                Review your findings against the patient's actual hidden concerns.
            </p>
        </div>

        {/* Brief AI Feedback */}
        {comparison?.brief_feedback && (
          <Card className="mb-8 border-l-4 border-l-green-500 bg-green-50/30 dark:bg-green-900/10 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-bold text-green-800 dark:text-green-400 flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Performance Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                {comparison.brief_feedback}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            {/* Ground Truth */}
            <Card className="border-t-4 border-t-blue-500 shadow-md">
                <CardHeader className="bg-blue-50/50">
                    <CardTitle className="flex items-center gap-2 text-blue-800">
                        <Target className="h-5 w-5" />
                        Expected Findings (Ground Truth)
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                        <p className="text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                            {comparison?.ground_truth || "No ground truth available for this patient."}
                        </p>
                    </div>
                </CardContent>
            </Card>

            {/* User Input */}
            <Card className="border-t-4 border-t-orange-500 shadow-md">
                <CardHeader className="bg-orange-50/50">
                    <CardTitle className="flex items-center gap-2 text-orange-800">
                        <FileText className="h-5 w-5" />
                        Your Submitted Findings
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                        <p className="text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                            {comparison?.user_findings || "No findings were submitted for this session."}
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>

        <div className="flex justify-center gap-4 pt-4 border-t pt-8">
            <Button variant="outline" size="lg" onClick={handleReturnToChart}>
            <User className="h-4 w-4 mr-2" />
            Return to Study Dashboard
            </Button>
        </div>
      </div>
    </div>
  );
};

export default EvaluationPage;
